import DeleteIcon from "@mui/icons-material/Delete";
import EditLocationAltIcon from "@mui/icons-material/EditLocationAlt";
import { Box, Button, Chip, Collapse, Grid, TextField, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { isEqual } from "lodash";
import { useEffect, useMemo, useState } from "react";
import "react-resizable/css/styles.css";

import { useGlobalSnackbar } from "../../context/SnackbarContext";
import { MapComponent } from "../common/Maps";
import { SearcherAddressPredictiveComponent } from "../common/SearcherAddressPredictiveComponent";
import { colors } from "../ui/colors";
import { SASButtonStyles } from "../ui/styles/inputStyles";
import { useClients } from "./hooks/useClients";
import { mapClientAreas } from "./utils";

const ALLOWED_COVERAGE_KEYS = [
  "id",
  "address",
  "city",
  "state",
  "country",
  "zipCode",
  "zip5",
  "zipPlus4",
  "zipSource",
  "latitude",
  "longitude",
  "milesOfCoverageArea",
  "name",
  "color",
];

const NULLABLE_COVERAGE_KEYS = new Set(["zip5", "zipPlus4", "zipSource"]);

/** Five digits stay valid. ZIP+4 is optional and uses 12345-6789. */
const zipFieldsFromInput = (raw) => {
  const compact = String(raw ?? "").replace(/\s+/g, "");
  const plus4 = compact.match(/^(\d{5})-(\d{4})$/);
  if (plus4) {
    const zipPlus4 = `${plus4[1]}-${plus4[2]}`;
    return { zipCode: zipPlus4, zip5: plus4[1], zipPlus4, zipSource: "CAPTURED" };
  }
  const five = compact.match(/^(\d{5})$/);
  if (five) {
    return { zipCode: five[1], zip5: five[1], zipPlus4: null, zipSource: "DERIVED" };
  }
  return { zipCode: raw, zip5: null, zipPlus4: null, zipSource: null };
};

const zipFieldValue = (area) => area?.zipPlus4 || area?.zipCode || "";

const zipFieldError = (area) => {
  const value = zipFieldValue(area).replace(/\s+/g, "");
  return value.includes("-") && !/^\d{5}-\d{4}$/.test(value);
};

const sanitizeCoverageArea = (area) => {
  if (!area || typeof area !== "object") return area;
  const cleaned = {};
  ALLOWED_COVERAGE_KEYS.forEach((key) => {
    let val = area[key];
    if (key === "milesOfCoverageArea" && val === undefined && area.radius !== undefined) {
      val = area.radius;
    }
    if (NULLABLE_COVERAGE_KEYS.has(key) || (val !== null && val !== undefined)) {
      cleaned[key] = val ?? null;
    }
  });
  return cleaned;
};

export const ClientFormCoverages = ({
  clientAreas = null,
  entityAreas = null,
  clientUid = null,
  entityUid = null,
  onUpdate = null,
  onClose = null,
  sx = {},
  allowUpdate = true,
  mode = "update", // "create" | "update" | "view"
  address = null,
  initialSearchAddress = null,
  clientForm = null,
  coverageAreas = [],
  setCoverageAreas = null,
  coverageAreaSearch = "",
  setCoverageAreaSearch = null,
  focusCoverageArea = null,
  setFocusCoverageArea = null,
  onSubmitCreate = null,
  /** Optional override for the save action. Receives (uid, areas[]) and must return { ok: boolean }. */
  onSaveCoverageAreas = null,
}) => {
  const isView = mode === "view";
  const isCreate = mode === "create";
  const isUpdate = mode === "update";

  const targetAreas = entityAreas || clientAreas || [];
  const targetUid = entityUid || clientUid;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { editClientCoverageAreas } = useClients();
  const { showSnackbar } = useGlobalSnackbar();

  const [updateAreas, setUpdateAreas] = useState(() => mapClientAreas(targetAreas) || []);
  const [focusArea, setFocusArea] = useState(() => (targetAreas?.[0] ? mapClientAreas([targetAreas[0]])?.[0] : null));
  const [updateCoverageAreaSearch, setUpdateCoverageAreaSearch] = useState("");

  useEffect(() => {
    if (!isUpdate && !isView) return;
    const mapped = mapClientAreas(targetAreas) || [];
    setUpdateAreas(mapped);
    setFocusArea((prev) => {
      if (prev?.id) {
        const restored = mapped.find((a) => a.id === prev.id);
        if (restored) return restored;
      }
      return mapped?.[0] ?? null;
    });
  }, [targetUid, isUpdate, isView, targetAreas]);

  useEffect(() => {
    const defaultAddress =
      initialSearchAddress ||
      address ||
      (clientForm?.address
        ? `${clientForm.address || ""}${clientForm.city ? ", " + clientForm.city : ""}${clientForm.state ? ", " + clientForm.state : ""}${clientForm.zipCode ? " " + clientForm.zipCode : ""}`.trim()
        : "");

    if (defaultAddress && (!clientAreas || clientAreas.length === 0)) {
      if (isCreate) {
        if (!coverageAreaSearch) {
          setCoverageAreaSearch?.(defaultAddress);
        }
      } else {
        if (!updateCoverageAreaSearch) {
          setUpdateCoverageAreaSearch(defaultAddress);
        }
      }
    }
  }, [clientAreas, address, initialSearchAddress, clientForm, isCreate, coverageAreaSearch, setCoverageAreaSearch]);

  const canEdit = allowUpdate && !isView;

  const coverageListSx = useMemo(
    () => ({
      mt: 2,
      width: "100%",
      maxWidth: "100%",
      maxHeight: { xs: 110, sm: 140, md: 160 },
      overflowY: "auto",
      overflowX: "hidden",
      borderRadius: 2,
      backgroundColor: "var(--fx-surface-base, #FFFFFF)",
      p: 1,
      display: "flex",
      flexWrap: "wrap",
      alignContent: "flex-start",
      scrollbarGutter: "stable",
    }),
    [],
  );

  const chipSx = (isFocused) => ({
    m: 0.5,
    px: 1.5,
    py: 0.5,
    borderRadius: 2,
    fontSize: "0.85rem",
    fontWeight: 500,
    bgcolor: isFocused ? colors.orange[50] : "var(--fx-surface-base, #FFFFFF)",
    color: colors.gray[800],
    border: `1px solid ${isFocused ? colors.orange[400] : colors.gray[200]}`,
    boxShadow: isFocused ? "0 2px 6px rgba(255, 152, 0, 0.3)" : "0 1px 3px rgba(0,0,0,0.08)",
    transition: "all 0.2s ease",
    "&:hover": canEdit
      ? {
          bgcolor: colors.orange[100],
          borderColor: colors.orange[400],
          transform: "translateY(-2px)",
          boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
        }
      : undefined,
  });

  const deleteIcon = !isView ? <DeleteIcon sx={{ color: colors.orange[400], "&:hover": { color: colors.orange[600] } }} /> : undefined;

  const AREA_FIELDS = [
    { key: "address", label: "Address", xs: 12, md: 12 },
    { key: "city", label: "City", xs: 12, md: 3 },
    { key: "state", label: "State", xs: 12, md: 3 },
    { key: "zipCode", label: "Zip Code", xs: 12, md: 3, helper: "Optional ZIP+4: 57049-1234" },
    { key: "country", label: "Country", xs: 12, md: 3 },
  ];

  const patchAreaField = (area, field, value) =>
    field === "zipCode" ? { ...area, ...zipFieldsFromInput(value) } : { ...area, [field]: value };

  const handleUpdateAreaField = (field, value) => {
    setFocusArea((prev) => patchAreaField(prev, field, value));
    setUpdateAreas((prev) => prev.map((a) => (a.id === focusArea?.id ? patchAreaField(a, field, value) : a)));
  };

  const handleCreateAreaField = (field, value) => {
    const updated = patchAreaField(focusCoverageArea, field, value);
    setFocusCoverageArea?.(updated);
    setCoverageAreas?.((prev) =>
      prev.map((a) => (a.latitude === focusCoverageArea?.latitude && a.longitude === focusCoverageArea?.longitude ? updated : a)),
    );
  };

  const handleCreateFocus = (area) => setFocusCoverageArea?.(area);
  const handleCreateDelete = (area) => {
    setCoverageAreas?.(coverageAreas.filter((a) => !(a.latitude === area.latitude && a.longitude === area.longitude)));
    setFocusCoverageArea?.((prev) => (prev?.latitude === area.latitude && prev?.longitude === area.longitude ? null : prev));
  };
  const handleCreateSubmit = (e) => {
    e.preventDefault();
    if (isView) return;
    onSubmitCreate?.();
  };

  const handleUpdateFocus = (area) => setFocusArea(area);
  const handleUpdateDelete = (area) => {
    setUpdateAreas((prev) => prev.filter((a) => a.id !== area.id));
    setFocusArea((prev) => (prev?.id === area.id ? null : prev));
  };

  const handleSaveUpdate = async () => {
    if (!canEdit) return;

    try {
      const cleanedUpdateAreas = (updateAreas || []).map(sanitizeCoverageArea);

      const res = onSaveCoverageAreas
        ? await onSaveCoverageAreas(targetUid, cleanedUpdateAreas)
        : await editClientCoverageAreas(targetUid, cleanedUpdateAreas);
      const ok = res?.ok !== false;

      if (ok) {
        if (!onSaveCoverageAreas) {
          showSnackbar("Locations updated successfully", "success");
        }
        await onUpdate?.();
        onClose?.();
      } else {
        if (!onSaveCoverageAreas) {
          showSnackbar("Update failed. Please try again.", "error");
        }
      }
    } catch {
      if (!onSaveCoverageAreas) {
        showSnackbar("Update failed. Please try again.", "error");
      }
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
      {isCreate && (
        <>
          <SearcherAddressPredictiveComponent
            store={coverageAreaSearch}
            storeAction={setCoverageAreaSearch}
            onSelectStore={coverageAreas}
            onSelectStoreAction={setCoverageAreas}
            disabled={isView}
            disableLoadScript={true}
            label="Search or type a full address"
            onSelectedLocation={(area) => setFocusCoverageArea?.(area)}
          />

          <MapComponent
            width="100%"
            height={isMobile ? 300 : 450}
            store={coverageAreas}
            storeAction={setCoverageAreas}
            focusStore={focusCoverageArea}
            disableLoadScript={true}
            sx={{
              mr: 2,
              borderRadius: 2,
              overflow: "hidden",
              border: `1px solid ${colors.orange?.[100] || "#FFE0B2"}`,
              ...sx,
            }}
          />

          <Box sx={{ mr: 2 }}>
            <Box id="client-form-coverage-areas" component="form" sx={coverageListSx} onSubmit={handleCreateSubmit}>
              {coverageAreas?.map((area) => (
                <Chip
                  key={area.id ?? area.address}
                  label={area.address}
                  onClick={() => handleCreateFocus(area)}
                  onDelete={canEdit ? () => handleCreateDelete(area) : undefined}
                  deleteIcon={deleteIcon}
                  sx={chipSx(isEqual(focusCoverageArea, area))}
                />
              ))}
            </Box>

            <Collapse in={canEdit && !!focusCoverageArea} unmountOnExit>
              <Box
                sx={{
                  mt: 2,
                  mb: 1,
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${colors.orange[200]}`,
                  backgroundColor: colors.orange[50],
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <EditLocationAltIcon sx={{ color: colors.orange[500], fontSize: 18 }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: colors.orange[700] }}>
                    Edit area details {focusCoverageArea?.address || "selected area"}
                  </Typography>
                </Box>
                <Grid container spacing={1.5}>
                  {AREA_FIELDS.map(({ key, label, xs, md, helper }) => (
                    <Grid item xs={xs} md={md} key={key}>
                      <TextField
                        size="small"
                        fullWidth
                        label={label}
                        placeholder={key === "zipCode" ? "57049 or 57049-1234" : undefined}
                        value={key === "zipCode" ? zipFieldValue(focusCoverageArea) : focusCoverageArea?.[key] || ""}
                        error={key === "zipCode" && zipFieldError(focusCoverageArea)}
                        helperText={key === "zipCode" ? (zipFieldError(focusCoverageArea) ? "Use 12345-6789" : helper) : undefined}
                        onChange={(e) => handleCreateAreaField(key, e.target.value)}
                        sx={{ backgroundColor: "var(--fx-surface-base, #FFFFFF)", borderRadius: 1 }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Collapse>
          </Box>
        </>
      )}

      {(isUpdate || isView) && (
        <>
          {canEdit && (
            <Box sx={{ p: 1 }}>
              <SearcherAddressPredictiveComponent
                store={updateCoverageAreaSearch}
                storeAction={setUpdateCoverageAreaSearch}
                onSelectStore={updateAreas}
                onSelectStoreAction={setUpdateAreas}
                disableLoadScript={true}
                label="Search or type a full address"
                onSelectedLocation={(area) => setFocusArea(area)}
              />
            </Box>
          )}

          <MapComponent
            width="100%"
            height={isMobile ? 300 : 460}
            store={updateAreas}
            focusStore={focusArea}
            setLocalStore={canEdit ? setUpdateAreas : undefined}
            onDelete={canEdit ? handleUpdateDelete : undefined}
            sx={{ borderRadius: 2, overflow: "hidden", ...sx }}
            disableLoadScript={true}
          />

          <Box sx={coverageListSx}>
            {updateAreas?.map((area) => (
              <Chip
                key={area.id ?? area.address}
                label={area.address}
                onClick={() => handleUpdateFocus(area)}
                onDelete={canEdit ? () => handleUpdateDelete(area) : undefined}
                deleteIcon={deleteIcon}
                sx={chipSx(isEqual(focusArea, area))}
              />
            ))}
          </Box>

          <Collapse in={canEdit && !!focusArea} unmountOnExit>
            <Box
              sx={{
                mt: 1,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${colors.orange[200]}`,
                backgroundColor: colors.orange[50],
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <EditLocationAltIcon sx={{ color: colors.orange[500], fontSize: 18 }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: colors.orange[700] }}>
                  Edit area details {focusArea?.address || "selected area"}
                </Typography>
              </Box>
              <Grid container spacing={1.5}>
                {AREA_FIELDS.map(({ key, label, xs, md, helper }) => (
                  <Grid item xs={xs} md={md} key={key}>
                    <TextField
                      size="small"
                      fullWidth
                      label={label}
                      placeholder={key === "zipCode" ? "57049 or 57049-1234" : undefined}
                      value={key === "zipCode" ? zipFieldValue(focusArea) : focusArea?.[key] || ""}
                      error={key === "zipCode" && zipFieldError(focusArea)}
                      helperText={key === "zipCode" ? (zipFieldError(focusArea) ? "Use 12345-6789" : helper) : undefined}
                      onChange={(e) => handleUpdateAreaField(key, e.target.value)}
                      sx={{ backgroundColor: "var(--fx-surface-base, #FFFFFF)", borderRadius: 1 }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Collapse>
        </>
      )}

      {allowUpdate && isUpdate && (
        <Box sx={{ display: "flex", justifyContent: "center", alignContent: "center" }}>
          <Button sx={{ ...SASButtonStyles, maxWidth: 300 }} disabled={!canEdit} onClick={handleSaveUpdate}>
            Save locations
          </Button>
        </Box>
      )}
    </Box>
  );
};

export { ClientFormCoverages as EntityFormCoverage };
